import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { LogOut, KeyRound, Check, Sun, Moon, Laptop } from 'lucide-react';
import { api } from '../lib/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTema } from '../contexts/ThemeContext.tsx';
import { Botao, Campo, Segmentado, Card } from '../components/ui/index.tsx';
import { formatarCpf, iniciais } from '../lib/format.ts';

export default function Perfil() {
  const { usuario, sair } = useAuth();
  const { tema, setTema } = useTema();
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const trocar = useMutation({
    mutationFn: () => api.post('/auth/trocar-senha', { senhaAtual: atual, senhaNova: nova }),
    onSuccess: () => {
      setAtual('');
      setNova('');
      setConfirma('');
      setErro(null);
    },
    onError: (e) => setErro(e instanceof Error ? e.message : 'Não foi possível trocar a senha.'),
  });

  function aoEnviar(e: FormEvent) {
    e.preventDefault();
    if (nova !== confirma) {
      setErro('A confirmação não confere com a nova senha.');
      return;
    }
    if (nova.length < 10) {
      setErro('A nova senha precisa de pelo menos 10 caracteres.');
      return;
    }
    setErro(null);
    trocar.mutate();
  }

  if (!usuario) return null;

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex items-center gap-4 p-5">
        <div className="grid size-11 shrink-0 place-items-center rounded-full border border-[var(--border)] bg-[var(--bg-subtle)] text-xs font-medium text-[var(--muted)]">
          {iniciais(usuario.nome)}
        </div>
        <div className="min-w-0">
          <h2 className="truncate text-base font-medium text-[var(--fg)]">{usuario.nome}</h2>
          <p className="truncate text-sm text-[var(--muted)]">
            {usuario.email ?? formatarCpf(usuario.cpf)}
          </p>
          <p className="mt-0.5 text-xs capitalize text-[var(--faint)]">
            {usuario.papel.replace('_', ' ')}
            {usuario.codigoVoluntario && ` · voluntário ${usuario.codigoVoluntario}`}
          </p>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="mb-4 text-sm font-medium text-[var(--fg)]">
          Aparência
        </h3>
        <Segmentado
          rotulo="Tema"
          valor={tema}
          onChange={setTema}
          opcoes={[
            { valor: 'sistema', rotulo: 'Sistema' },
            { valor: 'claro', rotulo: 'Claro' },
            { valor: 'escuro', rotulo: 'Escuro' },
          ]}
        />
        <p className="mt-3 flex items-center gap-2 text-xs text-[var(--faint)]">
          {tema === 'sistema' ? <Laptop size={13} /> : tema === 'claro' ? <Sun size={13} /> : <Moon size={13} />}
          {tema === 'sistema'
            ? 'Acompanha o tema do seu aparelho.'
            : `Sempre no tema ${tema}.`}
        </p>
      </Card>

      <Card className="p-5">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-medium text-[var(--fg)]">
          <KeyRound size={13} /> Trocar senha
        </h3>

        <form onSubmit={aoEnviar} className="flex flex-col gap-4" noValidate>
          <Campo
            rotulo="Senha atual"
            type="password"
            autoComplete="current-password"
            required
            value={atual}
            onChange={(e) => setAtual(e.target.value)}
          />
          <Campo
            rotulo="Nova senha"
            type="password"
            autoComplete="new-password"
            required
            value={nova}
            onChange={(e) => setNova(e.target.value)}
            placeholder="mínimo de 10 caracteres"
          />
          <Campo
            rotulo="Confirme a nova senha"
            type="password"
            autoComplete="new-password"
            required
            value={confirma}
            onChange={(e) => setConfirma(e.target.value)}
            erro={erro ?? undefined}
          />

          {trocar.isSuccess && (
            <p className="flex items-center gap-2 text-sm font-semibold text-[var(--success)]">
              <Check size={16} /> Senha atualizada.
            </p>
          )}

          <Botao type="submit" variante="primario" tamanho="lg" carregando={trocar.isPending}>
            Salvar nova senha
          </Botao>
        </form>
      </Card>

      <Botao
        variante="perigo"
        tamanho="lg"
        larguraTotal
        icone={<LogOut size={17} />}
        onClick={() => void sair()}
        
      >
        Sair da conta
      </Botao>
    </div>
  );
}
